import type {
  FieldCatalogue,
  FieldCatalogueEntry,
  FindingSeverity,
  FindingView,
  RequestPanel as Panel,
  RequestFieldView,
} from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleCheck, Info, OctagonAlert, Pencil, Plus, Send, Sparkles, TriangleAlert } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Language } from "@/i18n";
import { ClassificationEditor } from "./classification-editor";
import { DocumentList } from "./document-list";
import { FieldEditor } from "./field-editor";
import { displayValue } from "./field-format";
import { RequestStatusBadge } from "./request-status-badge";

const SEVERITY_ICONS: Record<FindingSeverity, typeof Info> = {
  INFO: Info,
  WARN: TriangleAlert,
  HIGH: OctagonAlert,
};

/**
 * Findings the requester sees: open ones except SYSTEM_NOTE, which only records for the buyer
 * how a value was derived; a point their answer closed (WP-2.7) is gone. Field keys in a message
 * are replaced by their labels.
 */
export function requesterFindings(findings: FindingView[]): FindingView[] {
  return findings.filter((f) => f.type !== "SYSTEM_NOTE" && f.resolvedAt === null);
}

function withLabels(message: string, catalogue: FieldCatalogue | undefined, language: Language): string {
  if (!catalogue) return message;
  return message.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (key) => {
    const entry = catalogue.fields.find((f) => f.key === key);
    return entry ? `“${entry.labels[language]}”` : key;
  });
}

/**
 * The live request panel (ADR-0014): classification, progress against the mandatory elements,
 * what is still needed, findings and the values captured so far, each editable in place by the
 * requester. Updated from `case.updated`, edits and periodic refreshes — never by a reload.
 */
export function RequestPanel({
  panel,
  catalogue,
  canEdit,
  editBlocked,
  suggestSubmit = false,
}: {
  panel: Panel;
  catalogue: FieldCatalogue | undefined;
  /** The viewer is the requester and the request is open. */
  canEdit: boolean;
  /** A reply is being written: edits wait (the API would answer 409). */
  editBlocked: boolean;
  /** The last turn's model hint: highlight Review & submit (it never submits). */
  suggestSubmit?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const titleId = useId();
  const entries = new Map(catalogue?.fields.map((f) => [f.key, f]));
  const fields = new Map(panel.fields.map((f) => [f.key, f]));
  const { gate } = panel;
  const total = gate.applicableMandatory.length;
  const done = total - gate.openMandatory.length;
  const recorded = panel.fields.filter((f) => !gate.openMandatory.includes(f.key));
  const [classifying, setClassifying] = useState(false);
  const changeButton = useRef<HTMLButtonElement>(null);
  // Focus returns to "Change" once the editor has closed and the button is mounted again — in an
  // effect, after the commit (a requestAnimationFrame could run before it and find no button).
  const returnFocus = useRef(false);
  useEffect(() => {
    if (classifying || !returnFocus.current) return;
    returnFocus.current = false;
    changeButton.current?.focus();
  }, [classifying]);
  // Once classified, open elements that "submit anyway" cannot skip are marked as required.
  const required = new Set(panel.engagementShape ? gate.openMandatory.filter((k) => !gate.skippable.includes(k)) : []);
  // A saved element moves from "Still needed" to "Details so far": once the refreshed panel shows
  // it there, focus goes to its Edit button (the button it was opened from no longer exists).
  const [focusAfterSave, setFocusAfterSave] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check on every panel refresh.
  useEffect(() => {
    if (!focusAfterSave) return;
    const button = document.querySelector<HTMLButtonElement>(
      `button[data-field-button="${focusAfterSave}"][data-has-value="true"]`,
    );
    if (button) {
      button.focus();
      setFocusAfterSave(null);
    }
  }, [focusAfterSave, panel]);

  const row = (key: string, field: RequestFieldView | undefined) => (
    <FieldRow
      key={key}
      requestId={panel.id}
      fieldKey={key}
      entry={entries.get(key)}
      field={field}
      canEdit={canEdit}
      editBlocked={editBlocked}
      required={required.has(key)}
      onSaved={setFocusAfterSave}
    />
  );

  const neededList = (
    <div className="divide-y divide-border">{gate.openMandatory.map((key) => row(key, fields.get(key)))}</div>
  );

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <div className="rounded-lg bg-surface p-4 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id={titleId} className="text-lg font-bold text-primary">
            {t("requests.panel.title")}
          </h2>
          <RequestStatusBadge status={panel.status} />
        </div>
        <p className="mt-1 text-sm text-fg-muted">
          <span className="tabular">{panel.reference}</span>
          {panel.title ? ` · ${panel.title}` : null}
        </p>
        <div className="mt-3 flex items-start justify-between gap-2 text-sm">
          <p>
            <span className="font-medium text-fg">{t("requests.panel.classification")}: </span>
            {panel.engagementShape ? (
              <span>
                {t(`requests.panel.shapes.${panel.engagementShape}`)}
                {panel.sizeTier ? ` · ${t(`requests.panel.tiers.${panel.sizeTier}`)}` : null}
                {panel.classificationSource === "REQUESTER" ? (
                  <span className="text-fg-muted"> ({t("requests.classification.setByYou")})</span>
                ) : null}
              </span>
            ) : (
              <span className="text-fg-muted">{t("requests.panel.notClassified")}</span>
            )}
          </p>
          {canEdit && !classifying ? (
            <button
              ref={changeButton}
              type="button"
              disabled={editBlocked}
              className="inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
              aria-label={t("requests.classification.change")}
              onClick={() => setClassifying(true)}
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
              {t("requests.classification.changeShort")}
            </button>
          ) : null}
        </div>
        {classifying ? (
          <ClassificationEditor
            panel={panel}
            onClose={() => {
              returnFocus.current = true;
              setClassifying(false);
            }}
          />
        ) : null}
        {total > 0 ? (
          <div className="mt-3">
            <p className="text-sm text-fg">{t("requests.panel.progress", { done, total })}</p>
            <div
              role="progressbar"
              aria-label={t("requests.panel.progressLabel")}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={done}
              aria-valuetext={t("requests.panel.progress", { done, total })}
              className="mt-1.5 h-2 overflow-hidden rounded-full bg-bg"
            >
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-200"
                style={{ width: `${Math.round((done / total) * 100)}%` }}
              />
            </div>
          </div>
        ) : null}
        {panel.submittedAt ? (
          <p className="mt-3 text-sm text-fg">
            {t("requests.submit.submittedOn", {
              date: new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
                new Date(panel.submittedAt),
              ),
            })}
          </p>
        ) : null}
        {canEdit ? <SubmitAction panel={panel} suggestSubmit={suggestSubmit} /> : null}
      </div>

      {gate.openMandatory.length > 0 ? (
        <PanelSection title={t("requests.panel.stillNeeded")}>
          {panel.engagementShape ? (
            neededList
          ) : (
            // Unclassified: the gate's provisional set (ADR-0016) — collapsed until the type is known.
            <details>
              <summary className="cursor-pointer text-sm font-semibold text-link">
                {t("requests.panel.showNeeded", { count: gate.openMandatory.length })}
              </summary>
              <p className="mt-2 text-sm text-fg-muted">{t("requests.panel.provisionalNeeded")}</p>
              {neededList}
            </details>
          )}
        </PanelSection>
      ) : null}

      {requesterFindings(panel.findings).length > 0 ? (
        <PanelSection title={t("requests.panel.findings")}>
          <FindingList findings={panel.findings} catalogue={catalogue} />
        </PanelSection>
      ) : null}

      {panel.documents.length > 0 ? (
        <PanelSection title={t("requests.documents.title")}>
          <DocumentList
            requestId={panel.id}
            documents={panel.documents}
            canRemove={canEdit}
            removeBlocked={editBlocked}
          />
        </PanelSection>
      ) : null}

      <PanelSection title={t("requests.panel.details")}>
        {recorded.length === 0 ? (
          <p className="text-sm text-fg-muted">{t("requests.panel.noDetails")}</p>
        ) : (
          <DetailsBySection fields={recorded} catalogue={catalogue} row={row} />
        )}
      </PanelSection>
    </section>
  );
}

/**
 * Where the requester stands with submission (ADR-0014): Review & submit when the gate passes,
 * Submit with missing details when only skippable elements are open, otherwise what to do first.
 */
function SubmitAction({ panel, suggestSubmit }: { panel: Panel; suggestSubmit: boolean }) {
  const { t } = useTranslation();
  const { gate } = panel;
  const to = `/requests/${panel.id}/submit`;
  if (gate.canSubmit) {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <p className="inline-flex items-center gap-1.5 self-start rounded-md bg-success-bg px-2 py-1 text-sm font-semibold text-success">
          <CircleCheck aria-hidden="true" className="h-4 w-4" />
          {t("requests.panel.ready")}
        </p>
        {suggestSubmit ? (
          <p className="flex items-center gap-1.5 text-sm text-fg">
            <Sparkles aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
            {t("requests.submit.suggested")}
          </p>
        ) : null}
        <Link
          to={to}
          className={clsx(
            "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-on-primary hover:bg-primary-hover",
            suggestSubmit && "ring-4 ring-focus/40",
          )}
        >
          <Send aria-hidden="true" className="h-4 w-4" />
          {t("requests.submit.reviewAndSubmit")}
        </Link>
      </div>
    );
  }
  if (gate.canSubmitAnyway) {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <p className="text-sm text-fg-muted">{t("requests.submit.missingHint", { count: gate.skippable.length })}</p>
        <Link
          to={to}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-border bg-surface px-4 text-sm font-semibold text-primary hover:bg-bg"
        >
          {t("requests.submit.submitWithMissing")}
        </Link>
      </div>
    );
  }
  return (
    <p className="mt-3 text-sm text-fg-muted">
      {panel.engagementShape && panel.sizeTier
        ? t("requests.submit.blockedHint")
        : t("requests.submit.unclassifiedHint")}
    </p>
  );
}

/**
 * Points for the buyer, keys shown as labels. The requester's view hides SYSTEM_NOTE and closed
 * points; the buyer's review page (WP-3.1) shows everything, closed points marked answered (WP-2.7).
 */
export function FindingList({
  findings,
  catalogue,
  audience = "requester",
}: {
  findings: FindingView[];
  catalogue: FieldCatalogue | undefined;
  audience?: "requester" | "buyer";
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  return (
    <ul className="flex flex-col gap-2">
      {(audience === "buyer" ? findings : requesterFindings(findings)).map((f) => {
        const Icon = f.resolvedAt ? CircleCheck : SEVERITY_ICONS[f.severity];
        return (
          <li
            key={f.id}
            className={`rounded-md border border-border border-l-4 p-3 ${f.resolvedAt ? "border-l-border" : "border-l-finding"}`}
          >
            <p
              className={`flex items-center gap-1.5 text-sm font-semibold ${f.resolvedAt ? "text-fg-muted" : "text-finding"}`}
            >
              <Icon aria-hidden="true" className="h-4 w-4" />
              {f.resolvedAt ? t("requests.panel.answered") : t(`requests.panel.severity.${f.severity}`)}
            </p>
            <p className={`mt-1 text-sm whitespace-pre-wrap break-words ${f.resolvedAt ? "text-fg-muted" : "text-fg"}`}>
              {withLabels(f.message, catalogue, language)}
            </p>
            {f.resolution ? (
              <p className="mt-1 text-sm whitespace-pre-wrap break-words text-fg">
                <span className="font-semibold">{t("requests.panel.resolution")}</span>{" "}
                {withLabels(f.resolution, catalogue, language)}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-fg-muted">{t(`requests.panel.origin.${f.origin}`)}</p>
          </li>
        );
      })}
    </ul>
  );
}

/** Recorded values grouped by registry section, in registry order. */
export function DetailsBySection({
  fields,
  catalogue,
  row,
}: {
  fields: RequestFieldView[];
  catalogue: FieldCatalogue | undefined;
  row: (key: string, field: RequestFieldView) => ReactNode;
}) {
  const { i18n } = useTranslation();
  const language = i18n.language as Language;
  const sectionOf = new Map(catalogue?.fields.map((f) => [f.key, f.section]));
  return (
    <>
      {(catalogue?.sections ?? []).map((section) => {
        const inSection = fields.filter((f) => sectionOf.get(f.key) === section.key);
        if (inSection.length === 0) return null;
        return (
          <div key={section.key} className="mt-3 first:mt-0">
            <h4 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{section.labels[language]}</h4>
            <div className="divide-y divide-border">{inSection.map((f) => row(f.key, f))}</div>
          </div>
        );
      })}
    </>
  );
}

function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-lg bg-surface p-4 shadow-card">
      <h3 id={id} className="mb-2 font-heading text-base font-semibold text-fg">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** One element: label, value (or editor), where the value came from, and Edit / Add. */
export function FieldRow({
  requestId,
  fieldKey,
  entry,
  field,
  canEdit,
  editBlocked,
  required = false,
  onSaved = () => undefined,
  audience = "requester",
}: {
  requestId: string;
  fieldKey: string;
  entry: FieldCatalogueEntry | undefined;
  field: RequestFieldView | undefined;
  canEdit: boolean;
  editBlocked: boolean;
  /** Open and not skippable: needed before the request can be submitted at all. */
  required?: boolean;
  onSaved?: (fieldKey: string) => void;
  /** Whose words "From you" means: the buyer's review page says "From the requester" (WP-3.1). */
  audience?: "requester" | "buyer";
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const labelId = useId();
  const [editing, setEditing] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const label = entry?.labels[language] ?? fieldKey;
  const value = field ? displayValue(field, entry, language, t) : null;
  const editable = canEdit && Boolean(entry?.editable);
  const hasValue = field?.status === "CAPTURED";

  function close(saved: boolean) {
    setEditing(false);
    if (saved) onSaved(fieldKey);
    // Cancelled: the row stays where it is; its button re-renders after the editor unmounts.
    else requestAnimationFrame(() => button.current?.focus());
  }

  return (
    <div className="py-2.5">
      <div className="flex items-start justify-between gap-2">
        <p id={labelId} className="min-w-0 text-sm font-medium break-words text-fg">
          {label}
          {required ? (
            <span className="ml-2 inline-block rounded-full bg-warning-bg px-2 py-0.5 text-xs font-semibold text-warning-fg">
              {t("requests.submit.requiredTag")}
            </span>
          ) : null}
        </p>
        {editable && !editing ? (
          <button
            ref={button}
            type="button"
            data-field-button={fieldKey}
            data-has-value={hasValue}
            disabled={editBlocked}
            title={editBlocked ? t("requests.panel.busyEditing") : undefined}
            aria-label={
              hasValue
                ? t("requests.panel.editField", { field: label })
                : t("requests.panel.addField", { field: label })
            }
            className="inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => setEditing(true)}
          >
            {hasValue ? (
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {hasValue ? t("requests.panel.edit") : t("requests.panel.add")}
          </button>
        ) : null}
      </div>
      <div>
        {editing && entry ? (
          <FieldEditor requestId={requestId} entry={entry} field={field} labelId={labelId} onClose={close} />
        ) : value === null ? (
          !editable && entry && !entry.editable ? (
            <p className="mt-0.5 text-sm text-fg-muted">{t("requests.panel.automatic")}</p>
          ) : null
        ) : (
          <>
            {Array.isArray(value) ? (
              <ul className="mt-0.5 list-disc pl-5 text-sm text-fg">
                {value.map((item, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: items are plain text and may repeat.
                  <li key={i} className="break-words">
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-0.5 text-sm whitespace-pre-wrap break-words text-fg">{value}</p>
            )}
            {/* For the buyer, "not provided" says it all: no "Derived" under it. */}
            {field && !(audience === "buyer" && field.status === "NOT_PROVIDED") ? (
              <p className="mt-0.5 text-xs text-fg-muted">
                {audience === "buyer" ? t(`review.source.${field.source}`) : t(`requests.panel.source.${field.source}`)}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
